/**
 * Reduz uma foto antes de subir.
 *
 * O envio do documento acontece no celular, logo depois do Pix, e é o ponto do funil em que já se
 * pagou e ainda não se está visível — a pior hora possível para uma barra de progresso travar. Uma
 * foto de câmera de celular tem 3–8 MB e 4000px de largura; nada disso ajuda alguém a ler um número
 * de RG na tela, e em 4G a diferença entre 6 MB e 300 KB é a diferença entre enviar e desistir.
 *
 * Fica em `services/` porque é regra sem React: a tela só entrega o File e recebe outro de volta.
 */

/** Lado maior da imagem depois da redução. Sobra folga para ler um documento com zoom na análise. */
const LADO_MAXIMO = 1600;

/** JPEG a 85%: abaixo disso o texto impresso começa a esfarelar, que é justamente o que se quer ler. */
const QUALIDADE = 0.85;

/**
 * Devolve um File novo, reduzido e em JPEG.
 *
 * Nunca rejeita por causa da redução: se o navegador não souber decodificar a imagem (HEIC de iPhone
 * antigo em navegador sem suporte, arquivo corrompido), devolve o original e deixa o backend decidir
 * — ele valida por magic bytes e responde com uma mensagem que a tela sabe mostrar. Falhar aqui
 * transformaria um problema de otimização em um bloqueio de cadastro.
 *
 * @param {File} arquivo
 * @param {{ ladoMaximo?: number, qualidade?: number }} opcoes
 * @returns {Promise<File>}
 */
export async function reduzirImagem(arquivo, { ladoMaximo = LADO_MAXIMO, qualidade = QUALIDADE } = {}) {
  if (!arquivo || !arquivo.type?.startsWith('image/')) return arquivo;

  try {
    const bitmap = await carregarImagem(arquivo);
    const { largura, altura } = dimensoesReduzidas(bitmap.width, bitmap.height, ladoMaximo);

    const canvas = document.createElement('canvas');
    canvas.width = largura;
    canvas.height = altura;

    const contexto = canvas.getContext('2d');
    if (!contexto) return arquivo;
    contexto.drawImage(bitmap, 0, 0, largura, altura);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', qualidade));
    if (!blob) return arquivo;

    // Se a "redução" engordou o arquivo (foto já pequena, PNG chapado virando JPEG), fica o original.
    if (blob.size >= arquivo.size) return arquivo;

    return new File([blob], trocarParaJpg(arquivo.name), { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return arquivo;
  }
}

/**
 * `createImageBitmap` resolve a maioria dos casos e é o caminho rápido; o `<img>` com object URL é o
 * fallback para navegadores que não o têm. O object URL é revogado sempre, inclusive no erro — sem
 * isso cada foto refeita deixaria os bytes presos na memória da aba.
 */
function carregarImagem(arquivo) {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(arquivo);
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(arquivo);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível ler a imagem'));
    };
    img.src = url;
  });
}

/** Proporção preservada: documento esticado fica ilegível, e é o que a pessoa do outro lado vai ler. */
function dimensoesReduzidas(largura, altura, ladoMaximo) {
  const maior = Math.max(largura, altura);
  if (maior <= ladoMaximo) return { largura, altura };

  const escala = ladoMaximo / maior;
  return {
    largura: Math.round(largura * escala),
    altura: Math.round(altura * escala),
  };
}

function trocarParaJpg(nome) {
  const base = (nome || 'documento').replace(/\.[^.]+$/, '');
  return `${base}.jpg`;
}
